/**
 * React hook for managing the lifecycle of a code runner worker.
 *
 * Handles:
 * - Worker creation and initialization based on language/runner type
 * - Message passing to/from worker
 * - Worker termination and respawn
 * - Timeout enforcement
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { getLanguageConfig, DEFAULT_LANGUAGE } from '../config/languages';
import type {
  WorkerRequest,
  WorkerResponse,
  RunCodeResult,
  RunTestsResult,
  RunnerType
} from '../types/worker';
import type { RuntimeStatus } from '../types/ui';

const EXECUTION_TIMEOUT_MS = 5000; // 5 seconds

export function useCodeRunner(languageId = DEFAULT_LANGUAGE) {
  const [status, setStatus] = useState<RuntimeStatus>('booting');
  const workerRef = useRef<Worker | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const messageHandlerRef = useRef<((response: WorkerResponse) => void) | null>(null);

  // Initialize worker
  const initWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
    }

    const langConfig = getLanguageConfig(languageId);
    const runnerType = langConfig.runner as RunnerType;

    // Create the universal runner worker
    const worker = new Worker(
      new URL('../workers/codeRunner.worker.ts', import.meta.url),
      { type: 'module' }
    );

    worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
      const response = event.data;

      if (response.type === 'READY') {
        setStatus('ready');
      } else if (response.type === 'ERROR') {
        setStatus('crashed');
        console.error('Worker error:', response.error);
      }

      if (messageHandlerRef.current) {
        messageHandlerRef.current(response);
      }
    };

    worker.onerror = (error) => {
      console.error('Worker error:', error);
      setStatus('crashed');
    };

    workerRef.current = worker;

    // Send INIT message with the specific runner type
    const initMessage: WorkerRequest = { 
      type: 'INIT',
      runnerType 
    };
    worker.postMessage(initMessage);
  }, [languageId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setStatus('booting');
    initWorker();

    return () => {
      workerRef.current?.terminate();
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [initWorker]);

  const clearExecutionTimeout = useCallback(() => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const setExecutionTimeout = useCallback(
    (onTimeout: () => void) => {
      clearExecutionTimeout();
      timeoutRef.current = window.setTimeout(onTimeout, EXECUTION_TIMEOUT_MS);
    },
    [clearExecutionTimeout]
  );

  const runCode = useCallback(
    (code: string): Promise<RunCodeResult> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || status !== 'ready') {
          reject(new Error('Worker not ready'));
          return;
        }

        setStatus('running');

        messageHandlerRef.current = (response: WorkerResponse) => {
          if (response.type === 'RUN_CODE_RESULT') {
            clearExecutionTimeout();
            setStatus('ready');
            messageHandlerRef.current = null;
            resolve(response);
          }
        };

        setExecutionTimeout(() => {
          messageHandlerRef.current = null;
          setStatus('booting');
          console.warn('Execution timeout - respawning worker');
          initWorker();

          resolve({
            type: 'RUN_CODE_RESULT',
            stdout: '',
            stderr: '',
            error: 'Execution timed out (5s limit)'
          });
        });

        workerRef.current.postMessage({ type: 'RUN_CODE', code });
      });
    },
    [status, clearExecutionTimeout, setExecutionTimeout, initWorker]
  );

  const runTests = useCallback(
    (
      code: string,
      functionName: string,
      publicTests: Array<{ name: string; args: unknown[]; expected: unknown }>
    ): Promise<RunTestsResult> => {
      return new Promise((resolve, reject) => {
        if (!workerRef.current || status !== 'ready') {
          reject(new Error('Worker not ready'));
          return;
        }

        setStatus('running');

        messageHandlerRef.current = (response: WorkerResponse) => {
          if (response.type === 'RUN_TESTS_RESULT') {
            clearExecutionTimeout();
            setStatus('ready');
            messageHandlerRef.current = null;
            resolve(response);
          }
        };

        setExecutionTimeout(() => {
          messageHandlerRef.current = null;
          setStatus('booting');
          console.warn('Execution timeout - respawning worker');
          initWorker();

          resolve({
            type: 'RUN_TESTS_RESULT',
            results: [],
            stdout: '',
            stderr: '',
            error: 'Execution timed out (5s limit)'
          });
        });

        workerRef.current.postMessage({ 
          type: 'RUN_TESTS', 
          code, 
          functionName, 
          publicTests 
        });
      });
    },
    [status, clearExecutionTimeout, setExecutionTimeout, initWorker]
  );

  const resetRuntime = useCallback(() => {
    clearExecutionTimeout();
    messageHandlerRef.current = null;
    initWorker();
  }, [clearExecutionTimeout, initWorker]);

  return {
    status,
    runCode,
    runTests,
    resetRuntime
  };
}
